import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../../../components/Navbar";
import Images from "../../../Images";
import {
  MessageSquare,
  X,
  AtSign,
  AlertCircle,
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ShieldCheck,
  ShieldAlert,
  BadgeCheck,
  AlertOctagon,
  Eye,
  Info,
  HelpCircle,
  ArrowRight,
  Gift,
} from "lucide-react";
import SendersRequest from "../../../requests/vendor/SendersRequest";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useSelector } from "react-redux";

const Sender = () => {
  const user = useSelector((state) => state?.user?.user) || null;
  const subscription_details = user?.subscription || null;
  const [showForm, setShowForm] = useState(false);
  const [senderID, setSenderID] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [senderDetails, setSenderDetails] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    senderId: null,
  });
  const [rejectReasonModal, setRejectReasonModal] = useState({
    isOpen: false,
    reason: "",
  });
  const [hasReachedLimit, setHasReachedLimit] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSender();
  }, []);

  const fetchSender = async () => {
    try {
      setIsLoading(true);
      const response = await SendersRequest.getAllSenders();
      const fetchedData = response?.data?.data || [];

      const firstSender = fetchedData.length > 0 ? fetchedData[0] : null;
      setSenderDetails(firstSender);
      const maxSenderIds =
        subscription_details?.subscription_plan?.features?.max_sender_ids || 1;
      setHasReachedLimit(fetchedData.length >= maxSenderIds);
    } catch (error) {
      toast.error(error.message);
      setSenderDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    if (!status) {
      return {
        className: `px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-all duration-200 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border border-gray-200 shadow-sm shadow-gray-100 hover:shadow-md hover:scale-105`,
        icon: <AlertOctagon size={16} className="text-gray-500" />,
        label: "Unknown",
      };
    }

    const baseStyle =
      "px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-all duration-200";

    const styles = {
      approved: {
        className: `${baseStyle} bg-gradient-to-r from-green-50 to-green-100 text-green-700 border border-green-200 shadow-sm shadow-green-100 hover:shadow-md hover:scale-105`,
        icon: (
          <CheckCircle2 size={16} className="text-green-500 animate-pulse" />
        ),
        label: "Approved",
      },
      rejected: {
        className: `${baseStyle} bg-gradient-to-r from-red-50 to-red-100 text-red-700 border border-red-200 shadow-sm shadow-red-100 hover:shadow-md hover:scale-105`,
        icon: <XCircle size={16} className="text-red-500" />,
        label: "Rejected",
      },
      pending: {
        className: `${baseStyle} bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700 border border-yellow-200 shadow-sm shadow-yellow-100 hover:shadow-md hover:scale-105`,
        icon: <Clock size={16} className="text-yellow-500 animate-spin" />,
        label: "Pending",
      },
      suspended: {
        className: `${baseStyle} bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700 border border-orange-200 shadow-sm shadow-orange-100 hover:shadow-md hover:scale-105`,
        icon: <AlertTriangle size={16} className="text-orange-500" />,
        label: "Suspended",
      },
      verified: {
        className: `${baseStyle} bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border border-blue-200 shadow-sm shadow-blue-100 hover:shadow-md hover:scale-105`,
        icon: <ShieldCheck size={16} className="text-blue-500" />,
        label: "Verified",
      },
    };

    const statusKey = status.toLowerCase();
    return (
      styles[statusKey] || {
        className: `${baseStyle} bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border border-gray-200 shadow-sm shadow-gray-100 hover:shadow-md hover:scale-105`,
        icon: <AlertOctagon size={16} className="text-gray-500" />,
        label: status,
      }
    );
  };

  const validateSenderID = (value) => {
    if (/^\d+$/.test(value)) {
      setError("Sender ID cannot contain only numbers");
      return false;
    }
    if (!/^[a-zA-Z0-9 ]*$/.test(value)) {
      setError("Only alphabets, numbers, and spaces are allowed");
      return false;
    }
    setError("");
    return true;
  };

  const handleSenderIDChange = (e) => {
    const value = e.target.value.slice(0, 11);
    setSenderID(value);
    validateSenderID(value);
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      if (senderDetails) {
        // Only allow editing if the sender is in pending state
        if (senderDetails.status?.toLowerCase() !== "pending") {
          toast.info(
            "You can only edit a Sender ID when it's in pending state"
          );
          setIsLoading(false);
          return;
        }

        await SendersRequest.updateSender(senderDetails.sender_slug, {
          sender: senderID,
        });
        fetchSender();
        toast.success("Sender ID updated successfully");
      } else {
        await SendersRequest.createSender({
          sender: senderID,
        });
        fetchSender();
        toast.success("Sender ID created successfully");
      }
      setShowForm(false);
      setSenderID("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteModal({ isOpen: true, senderId: id });
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      await SendersRequest.deleteSender(deleteModal.senderId);
      fetchSender();
      toast.success("Sender ID deleted successfully");
      setDeleteModal({ isOpen: false, senderId: null });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewRejection = (item) => {
    setRejectReasonModal({
      isOpen: true,
      reason: item.reject_reason || "No reason provided",
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar page={"Sender ID Management"} />

      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white overflow-hidden"
      >
        {/* Background Pattern */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.07 }}
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2V6h4V4H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative px-6 py-10 mx-auto max-w-7xl sm:px-8 lg:px-12">
          <div className="flex flex-col items-center space-y-8 md:flex-row md:space-x-12 md:space-y-0">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 20,
                duration: 0.8,
              }}
              className="p-4 rounded-3xl shadow-lg backdrop-blur-lg bg-white/10"
            >
              <img
                src={Images.sms_sender}
                alt="sms-sender-id"
                className="object-contain w-36 h-36 transition-transform duration-300 hover:scale-105"
              />
            </motion.div>

            <div className="space-y-4 text-center md:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-2"
              >
                <h1 className="text-4xl font-bold tracking-tight">
                  Sender-ID Management
                </h1>
                <p className="max-w-md text-lg text-white/90">
                  Create and manage your Sender ID for all your SMS
                  communications
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col items-center space-y-3 w-full sm:flex-row sm:space-y-0 sm:space-x-4 sm:w-auto"
              >
                {!senderDetails && !hasReachedLimit && (
                  <button
                    onClick={() => setShowForm(true)}
                    className="w-full sm:w-auto px-6 py-2.5 bg-white text-[#FF6C6C] rounded-full font-medium hover:bg-opacity-90 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <AtSign size={18} />
                    <span>Create Sender ID</span>
                  </button>
                )}

                {hasReachedLimit && !senderDetails && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate("/pricing")}
                    className="flex items-center px-6 py-2.5 space-x-2 w-full font-medium text-white bg-gradient-to-r from-amber-500 to-amber-600 rounded-full border backdrop-blur-lg transition-opacity duration-200 sm:w-auto hover:opacity-90 border-white/20"
                  >
                    <BadgeCheck size={18} />
                    <span>Upgrade Plan</span>
                  </motion.button>
                )}

                <button
                  onClick={() => navigate("/offers/new")}
                  className="flex justify-center items-center px-6 py-2.5 space-x-2 w-full font-medium rounded-full backdrop-blur-lg transition-colors duration-200 sm:w-auto bg-white/10 hover:bg-white/20"
                >
                  <Gift size={18} />
                  <span>Create Deal</span>
                </button>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1 }}
          className="absolute top-0 right-0 -mt-20 -mr-20"
        >
          <div className="w-96 h-96 bg-white rounded-full" />
        </motion.div>
      </motion.div>

      {/* Create New Sender ID Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden w-full"
          >
            <div className="px-4 py-4 w-full sm:px-6">
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="relative p-4 w-full bg-white rounded-2xl border border-gray-100 shadow-xl sm:p-6"
              >
                <motion.button
                  onClick={() => {
                    setShowForm(false);
                    setSenderID("");
                  }}
                  whileHover={{ rotate: 90 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-3 right-3 p-2 rounded-full transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </motion.button>

                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center mb-4 space-x-3"
                >
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <AtSign size={24} className="text-[#FF6C6C]" />
                  </motion.div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    {senderDetails ? "Edit Sender ID" : "Create New Sender ID"}
                  </h2>
                </motion.div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="flex flex-col gap-4 items-start md:flex-row md:items-center"
                >
                  <div className="flex-grow space-y-1 w-full md:min-w-[300px] md:max-w-[400px]">
                    <label className="block text-sm font-medium text-gray-700">
                      Sender ID Name
                    </label>
                    <motion.div
                      className="relative"
                      whileTap={{ scale: 0.995 }}
                    >
                      <input
                        type="text"
                        value={senderID}
                        onChange={handleSenderIDChange}
                        className={`w-full pl-10 pr-4 py-2.5 border ${
                          error
                            ? "border-red-300 focus:ring-red-200"
                            : "border-gray-300 focus:ring-[#FF6C6C]/20"
                        } rounded-xl focus:ring-4 focus:border-[#FF6C6C] transition-all duration-200 outline-none`}
                        placeholder="Enter sender ID (max 11 characters)"
                      />
                      <div className="flex absolute inset-y-0 left-3 items-center pointer-events-none">
                        <MessageSquare size={18} className="text-gray-400" />
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: error ? "auto" : 0,
                        opacity: error ? 1 : 0,
                      }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="flex items-center space-x-2 text-red-500">
                        <AlertCircle size={14} />
                        <span className="text-xs">{error}</span>
                      </div>
                    </motion.div>

                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-xs text-gray-500"
                    >
                      Must be alphanumeric and cannot contain only numbers
                    </motion.p>
                  </div>

                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="flex flex-col items-center space-y-3 w-full sm:flex-row sm:space-y-0 sm:space-x-4 md:w-auto md:self-start md:mt-7"
                  >
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowForm(false);
                        setSenderID("");
                      }}
                      className="px-5 py-2.5 w-full font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors duration-200 sm:w-auto hover:bg-gray-200"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-xl font-medium hover:opacity-90 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      disabled={!senderID || error || isLoading}
                      onClick={handleSubmit}
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                      ) : (
                        <>
                          <span>
                            {senderDetails ? "Update" : "Create"} Sender ID
                          </span>
                          <motion.div
                            animate={{ x: [0, 5, 0] }}
                            transition={{ repeat: Infinity, duration: 1.5 }}
                          >
                            <AtSign size={16} />
                          </motion.div>
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Section */}
      <div className="px-6 py-8 mx-auto max-w-7xl">
        {isLoading ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="w-12 h-12 border-4 border-[#FF6C6C] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {senderDetails ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-lg"
              >
                <div className="p-6 border-b border-gray-100">
                  <div className="flex flex-col justify-between md:flex-row md:items-center">
                    <div className="flex items-center mb-4 space-x-3 md:mb-0">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring" }}
                        className="p-3 bg-[#FF6C6C]/10 rounded-full"
                      >
                        <AtSign size={24} className="text-[#FF6C6C]" />
                      </motion.div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-800">
                          {senderDetails.sender}
                        </h2>
                        <p className="text-sm text-gray-500">
                          Created on{" "}
                          {moment(senderDetails.created_at).format(
                            "DD MMM, YYYY"
                          )}
                        </p>
                      </div>
                    </div>

                    <div
                      className={getStatusStyle(senderDetails.status).className}
                    >
                      {getStatusStyle(senderDetails.status).icon}
                      <span>{getStatusStyle(senderDetails.status).label}</span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Info Card */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                      className="p-5 space-y-4 bg-blue-50 rounded-xl border border-blue-100"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-full">
                          <Info size={18} className="text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-blue-800">
                          Sender ID Information
                        </h3>
                      </div>

                      <ul className="space-y-3">
                        <li className="flex items-start space-x-3">
                          <div className="p-1 mt-0.5 bg-blue-100 rounded-full">
                            <HelpCircle size={14} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-blue-700">
                              A Sender ID is a unique identifier that appears as
                              the sender when you send SMS messages to your
                              customers.
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start space-x-3">
                          <div className="p-1 mt-0.5 bg-blue-100 rounded-full">
                            <HelpCircle size={14} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-blue-700">
                              Once your Sender ID is approved, you cannot edit
                              it. Please contact support if you need to make
                              changes.
                            </p>
                          </div>
                        </li>
                      </ul>
                    </motion.div>

                    {/* Actions Card */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                      className="p-5 space-y-4 bg-gray-50 rounded-xl border border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gray-200 rounded-full">
                          <MessageSquare size={18} className="text-gray-700" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          Available Actions
                        </h3>
                      </div>

                      <div className="space-y-3">
                        {senderDetails.status?.toLowerCase() === "pending" && (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              setSenderID(senderDetails.sender);
                              setShowForm(true);
                            }}
                            className="flex justify-between items-center px-4 py-2.5 w-full bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:shadow-md"
                          >
                            <div className="flex items-center space-x-3">
                              <Pencil size={16} className="text-blue-500" />
                              <span className="font-medium text-gray-700">
                                Edit Sender ID
                              </span>
                            </div>
                            <ArrowRight size={16} className="text-gray-400" />
                          </motion.button>
                        )}

                        {senderDetails.status?.toLowerCase() === "rejected" && (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleViewRejection(senderDetails)}
                            className="flex justify-between items-center px-4 py-2.5 w-full bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:shadow-md"
                          >
                            <div className="flex items-center space-x-3">
                              <Eye size={16} className="text-purple-500" />
                              <span className="font-medium text-gray-700">
                                View Rejection Reason
                              </span>
                            </div>
                            <ArrowRight size={16} className="text-gray-400" />
                          </motion.button>
                        )}

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() =>
                            handleDeleteClick(senderDetails.sender_slug)
                          }
                          className="flex justify-between items-center px-4 py-2.5 w-full bg-white rounded-lg border border-red-100 shadow-sm transition-all hover:shadow-md hover:border-red-200"
                        >
                          <div className="flex items-center space-x-3">
                            <Trash2 size={16} className="text-red-500" />
                            <span className="font-medium text-red-600">
                              Delete Sender ID
                            </span>
                          </div>
                          <ArrowRight size={16} className="text-red-400" />
                        </motion.button>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col justify-center items-center p-12 space-y-6 text-center bg-white rounded-2xl border border-gray-200 shadow-lg"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 20 }}
                  className="p-6 bg-[#FF6C6C]/10 rounded-full"
                >
                  <AtSign size={48} className="text-[#FF6C6C]" />
                </motion.div>

                <div className="space-y-2 max-w-md">
                  <h2 className="text-2xl font-bold text-gray-800">
                    No Sender ID Found
                  </h2>
                  <p className="text-sm text-gray-600">
                    Create a Sender ID. This will help us notify subscribers
                    about new deals, enhancing customer engagement with your
                    deals. Your Sender ID will appear as the sender name when
                    alerts are delivered to your recipients' devices.
                  </p>
                </div>

                {!hasReachedLimit ? (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowForm(true)}
                    className="px-6 py-2.5 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-full font-medium hover:opacity-90 transition-opacity duration-200 flex items-center space-x-2"
                  >
                    <AtSign size={18} />
                    <span>Create Sender ID</span>
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate("/pricing")}
                    className="flex items-center px-6 py-2.5 space-x-2 font-medium text-white bg-gradient-to-r from-amber-500 to-amber-600 rounded-full transition-opacity duration-200 hover:opacity-90"
                  >
                    <BadgeCheck size={18} />
                    <span>Upgrade Plan</span>
                  </motion.button>
                )}
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteModal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-xl"
            >
              <motion.div
                initial={{ rotate: 10, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="flex justify-center mb-4"
              >
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle size={32} className="text-red-500" />
                </div>
              </motion.div>

              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="mb-6 text-center"
              >
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  Confirm Deletion
                </h3>
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this sender ID? This action
                  cannot be undone.
                </p>
              </motion.div>

              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex flex-col gap-2 sm:flex-row sm:gap-3"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    setDeleteModal({ isOpen: false, senderId: null })
                  }
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDeleteConfirm}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex justify-center items-center">
                      <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    </div>
                  ) : (
                    "Delete Sender ID"
                  )}
                </motion.button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rejection Reason Modal */}
      <AnimatePresence>
        {rejectReasonModal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-xl"
            >
              <motion.button
                whileHover={{ rotate: 90 }}
                onClick={() =>
                  setRejectReasonModal({ isOpen: false, reason: "" })
                }
                className="absolute top-4 right-4 p-1 text-gray-400 rounded-full hover:bg-gray-100"
              >
                <X size={20} />
              </motion.button>

              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="flex flex-col items-center"
              >
                <motion.div
                  initial={{ rotate: 10, scale: 0 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="mb-4"
                >
                  <div className="p-3 bg-red-100 rounded-full">
                    <XCircle size={32} className="text-red-500" />
                  </div>
                </motion.div>

                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Rejection Reason
                </h3>

                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="p-4 mt-4 w-full bg-gray-50 rounded-xl"
                >
                  <div className="flex items-start space-x-3">
                    <AlertOctagon
                      size={20}
                      className="flex-shrink-0 mt-1 text-red-500"
                    />
                    <p className="text-sm leading-relaxed text-gray-600">
                      {rejectReasonModal.reason}
                    </p>
                  </div>
                </motion.div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    setRejectReasonModal({ isOpen: false, reason: "" })
                  }
                  className="mt-6 px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl hover:opacity-90 transition-opacity duration-200"
                >
                  Close
                </motion.button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Sender;
